import React from 'react';
import {
  css,
  ThemeProvider as EmotionThemeProvider,
  Global,
} from '@emotion/react';
import _, { chain } from 'lodash';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import PricingTableHelper from '@bbkAdminComponents/experiences/pricing-pages-editors/helper/pricing-table-helper';
import {
  type ExperienceCardTemplate,
  type ExperienceFullWidthHeaderCard,
  type ExperienceHostedFooterCard,
  type ExperienceMultiRowSurveyCard,
  type ExperienceMultiSurveyCardSection,
  type ExperienceSimpleHeaderCard,
  type ExperienceSimplePleaCard,
  type ExperienceSimplePleaWithReturnCard,
  type PricingPageHeaderCard,
} from '@bbkAdminRedux/rtkq/experience.slice';
import { type PlacementLossAversionPosition } from '@bbkAdminRedux/rtkq/placements.utils';
import { type RestPricingPageLayout } from '@bbkAdminRedux/rtkq/pricing-pages.slice';
import {
  SurveyType,
  type RestMultiRowSurveyCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  type RestCard,
  type RestExperience,
} from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  RestModalAction,
  RestModalActionsAccepts,
  RestModalTemplates,
  type RestModalCard,
  type RestModalForwards,
  type RestOfferCardPack,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { FeatureFlagsE } from '@bbkAdminUtils/feature-flags';
import { type Maybe } from '@bbkAdminUtils/utility-types';
import { Loss } from '@bbkFunnel/components/loss/loss-aversion-cards';
import {
  Events as EventsClass,
  EventSource,
  type EventObject,
  type EventSyncObject,
  type NextStepHandler,
  type SubmissionConfig,
} from '@bbkFunnel/events';
import { ThemeKeys, type EmotionTheme } from '@bbkFunnel/pages/theme';
import { CANCEL_WITH_REFUND_MODAL_ID } from '@bbkFunnelComponents/constants';
import Container from '@bbkFunnelComponents/container';
import { HostedFooter } from '@bbkFunnelComponents/footer/hostedFooter';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { FullWidthHeader } from '@bbkFunnelComponents/header/header-full-width';
import { SimpleHeader } from '@bbkFunnelComponents/header/header-simple';
import { OfferCardpackRenderer } from '@bbkFunnelComponents/loss/offer-cardpack/offer-cardpack-renderer';
import {
  Modal,
  type FunnelModalOnCloseReason,
  type ModalHandleAnyInfo,
  type ModalOnClose,
} from '@bbkFunnelComponents/modal';
import { type CompetitorOtherTextHandler } from '@bbkFunnelComponents/modals/sections/competition-other-text';
import { LoadingOverlay } from '@bbkFunnelComponents/overlay';
import { Plea } from '@bbkFunnelComponents/plea/plea';
import { type CompetitorOtherTextClearHandler } from '@bbkFunnelComponents/survey/competition/competition-content';
import { type ReasonOtherTextClearHandler } from '@bbkFunnelComponents/survey/reasons/reasons-content';
import { type ReasonOtherHandler } from '@bbkFunnelComponents/survey/reasons/reasons-other-text';
import {
  SurveyMultiRow,
  type DirectFromSurveyCancelHandler,
  type SurveyMultiRowResponseHandler,
} from '@bbkFunnelComponents/survey-multi-row';
import { Warn } from '@bbkFunnelComponents/warn/warn';
import { FunnelLocalState } from '@bbkFunnelUtils/funnel-local-state';
import { embedFont, prefetchImages } from '@bbkFunnelUtils/funnel-utils';
import PricingPageHeader from '@bbkPricingPage/pricing-page-header';
import PricingTable from '@bbkPricingPage/pricing-table';
import { type PricingTable as PricingTableType } from '@bbkPricingPage/types/pricing-table-types';

import '@bbkFunnelStylesheets/index.scss';

type Props = RestExperience & {
  disableThemeProvider?: boolean;
  isLossAversionLoading?: boolean;
  lossAversionErrors?: PlacementLossAversionPosition[];

  // Fully resolved theme; locale resolution happens at the entry points.
  emotionTheme: EmotionTheme;

  // Lifecycle props
  sendLCOEventsToParent?: CallableFunction;
  isLifecycle?: boolean;

  // Pricing table props
  country?: string;
};

type State = {
  showModal: boolean;
  showLoader: boolean;
  showOpaqueLoader: boolean;

  closeModalReason?: FunnelModalOnCloseReason;

  modal: RestModalCard;
  survey: ExperienceMultiRowSurveyCard['provides'];
  surveyType: SurveyType;
  localState?: FunnelLocalState;
  presentation?: string;

  cards: Map<string, RestCard>;
  cardsByTemplate: Map<string, RestCard>;
  content: RestCard[]; // .filter(card.deploy === 'exit_survey')

  submitConfig: SubmissionConfig;
  surveySection: RestMultiRowSurveyCard;
  questions: ExperienceMultiSurveyCardSection[];

  hasShownRefundModal: boolean;
};

const Events = new EventsClass();

export type BackwardsObject = {
  name: 'submit_section_deflect_button';
  direction: 'backwards';
  type?: string;
  action: string;
  url?: string;
  validation?: string;
  form: RestModalForwards['sync'];
  component: {
    id: string;
    name: string;
  };
};

type TrackEventHandler = (
  e: { target: { type: string } },
  value: string | boolean,
  key: keyof ExperienceMultiRowSurveyCard['provides']
) => void;

export type ExecuteEventsArg = {
  events?: EventObject[];
  syncs?: EventSyncObject[];
  synchronous?: boolean;
};

class Funnel extends React.Component<Props, State> {
  static contextType = FunnelContext;

  static defaultProps = {
    integrations: {},
    company: {},
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      showModal: false,
      showLoader: false,
      showOpaqueLoader: false,
      modal: {} as RestModalCard,
      hasShownRefundModal: false,

      ...Funnel.processCards(props, true),
    };
  }

  static getDerivedStateFromProps(props: Props) {
    // FIXME: Replace with FC Funnel component asap, line bellow is undocumented
    // @ts-ignore
    const context = FunnelContext.Consumer._currentValue;

    return {
      ...(context.isPreview ? Funnel.processCards(props, true) : undefined),
    };
  }

  static processCards(props: Props, constructorCall?: boolean) {
    const { layout } = props;
    // KV map of the cards
    const cards = new Map(layout.cards.map((i) => [i.name, i]));
    const cardsByTemplate = new Map(layout.cards.map((i) => [i.template, i]));

    // need to make sure we get one of these
    const surveySection = (cards.get('multi_row_survey') ||
      {}) as ExperienceMultiRowSurveyCard;
    const questions = surveySection?.requires?.multi_row_survey || [];

    // Provides => items that need to be sent back from the survey
    const provides = {
      ...(surveySection?.provides || {}),
    } as ExperienceMultiRowSurveyCard['provides'];

    // initial survey load, this needs to now be -1 due to 0-10 sentiment change
    if (constructorCall && provides.sentiment === 0) {
      provides.sentiment = -1;
    }

    // Cards on the page
    const content = layout.cards.filter(
      (card) => card.deploy === 'exit_survey'
    );

    // Info if answers should be included during a sync cancel
    const submitConfig = _.get(surveySection, 'requires.forwards.sync', {});

    const { presentation } = layout;

    return {
      cards,
      cardsByTemplate,
      content,
      survey: provides,
      surveyType: SurveyType.multi_row_survey,
      surveySection,
      submitConfig,
      questions,
      presentation,
    };
  }

  componentDidMount() {
    const { layout, name, id, session, version, user, config } = this.props;
    const { isPreview, isBrandingPage, isPricingPage } = this.context;

    let { localState } = this.state;
    const { survey } = this.state;
    if (!localState) {
      localState = new FunnelLocalState({ id, name, session }, isPreview);
      this.setState({ localState });
    }

    const savedState = localState.loadSavedState();

    if (savedState) {
      this.setState(savedState);
    }

    localState.saveState(this.state);

    // Track page_load
    if (!isPricingPage) {
      this.executeEvents({
        events: [
          {
            session,
            id,
            name,
            event: 'page_loaded',
            version,
            app_id: (user && user.app_id) || null,
            user_id: _.get(user, 'user.id', null),
            properties: {
              survey_state: survey,
            },
          },
        ],
      });
    }

    // Add any custom fonts
    embedFont({ layout });

    prefetchImages({ layout });

    // Check for page loaded modal
    if (config?.page_load_modal && !isBrandingPage) {
      const { page_load_modal } = config;
      let launchModal;
      if (page_load_modal.card_name) {
        launchModal = layout.cards.find(
          (cd) => cd.name === page_load_modal.card_name
        );
      } else {
        launchModal = layout.cards.find(
          (cd) => cd.id === page_load_modal.card_id
        );
      }
      if (!_.isNil(launchModal)) {
        const modalName = launchModal.name;
        // if delay, then delay by X ms
        const delay = _.get(page_load_modal, 'delay', 0);
        setTimeout(() => {
          this.nextStep(modalName);
          this.continueFn();
        }, delay);
      } else {
        const error = `Initial page load modal could not be found. Please configure this experience correctly. \nCONFIG: ${JSON.stringify(
          page_load_modal
        )}`;
        console.warn(error);
      }
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { isLifecycle, session, version, user, layout } = this.props;
    const { closeModalReason, modal, showModal, survey, localState } =
      this.state;
    const { isPreview, isPricingPage, pricingPageProps } = this.context;

    if (isPreview && layout.cards !== prevProps.layout.cards) {
      this.setState(Funnel.processCards(this.props));
    }

    if (isPricingPage) {
      return;
    }
    // Save current state to LS
    localState?.saveState(this.state);

    // Send a track event for the following on a component update
    // modal_opened
    // modal_closed
    if (_.isObject(modal) && _.isObject(prevState.modal)) {
      // break out the sync values (if present),
      // but also include the full modal to log additionally
      const forwards = _.get(modal, 'requires.forwards.sync', {});
      const backwards = _.get(modal, 'requires.backwards.sync', {});
      const addlData = {
        modal,
        sync: { ...forwards },
        backwardsSync: { ...backwards },
      };

      const prevModalUndefined = !prevState.modal.name || !prevState.showModal;

      if (prevModalUndefined && showModal) {
        this.executeEvents({
          events: [
            {
              session,
              id: _.get(modal, 'id', 'no_id'),
              name: _.get(modal, 'name', undefined),
              event: 'modal_opened',
              version,
              card_version: _.get(modal, 'version'),
              app_id: _.get(user, 'app_id', null),
              user_id: _.get(user, 'user.id', null),
              properties: {
                survey_state: survey,
                ...addlData,
              },
            },
          ],
        });
      }

      if (
        !prevModalUndefined &&
        !showModal &&
        modal.name !== prevState.modal.name
      ) {
        const modalClosedEvt = {
          session,
          id: prevState.modal.id,
          name: prevState.modal.name,
          version,
          card_version: _.get(prevState.modal, 'version'),
          app_id: _.get(user, 'app_id', null),
          user_id: _.get(user, 'user.id', null),
          event: 'modal_closed',
          properties: {
            survey_state: survey,
            ...addlData,
          },
        };
        const modalClosedEvts = [modalClosedEvt];
        const modalCtas = collectForwards(prevState.modal);
        const modalCtasAreActionable = modalCtas.some((x) =>
          RestModalActionsAccepts.includes(x.value.action)
        );
        const isSuccessModal =
          prevState.modal.template === RestModalTemplates.modal_email_success;
        const addRejectedOfferEvt =
          isLifecycle && modalCtasAreActionable && !isSuccessModal;
        if (addRejectedOfferEvt && closeModalReason === 'x') {
          modalClosedEvts.push({
            ...modalClosedEvt,
            event: 'rejected_offer',
          });
        }
        this.executeEvents({
          events: modalClosedEvts,
        });
      }
    } else if (showModal && _.isNil(modal)) {
      console.error(
        '[BRIGHTBACK EXIT FUNNEL] No modal defined for this action. Check your configuration.'
      );
      this.setState({
        showModal: false,
      });
    }
  }

  // Track events for interaction with the survey
  handleTrackEvent: TrackEventHandler = (e, value, key) => {
    const { session, id, version, user } = this.props;
    const { cardsByTemplate, survey, surveyType } = this.state;
    // Events tracked:
    // radio_selected
    // select_selected
    // nps_selected
    // textarea_typed
    // checkbox_checked
    // checkbox_unchecked
    const event = 'radio_selected';
    const trackData: EventObject = {
      session,
      id,
      name: surveyType,
      event,
      version,
      app_id: (user && user.app_id) || null,
      card_version: cardsByTemplate.get('multi_row_survey')?.version,
      properties: {
        type: e.target.type,
        name: key,
        option: value,
        survey_state: survey,
      },
    };

    switch (key) {
      case 'competition':
        trackData.event = 'select_selected';
        break;
      case 'sentiment':
        trackData.event = 'nps_selected';
        break;
      case 'feedback':
        trackData.event = 'textarea_typed';
        break;
      case 'reason_other':
        trackData.event = 'reason_other';
        break;
      case 'competition_other':
        trackData.event = 'competition_other';
        break;
      case 'confirmation':
        trackData.event = value ? 'checkbox_checked' : 'checkbox_unchecked';
        break;
      default:
        break;
    }

    this.executeEvents({ events: [trackData] });
  };

  executeEvents = async (eventsAndSyncs: ExecuteEventsArg) => {
    const { isPreview, isPricingPage } = this.context;
    const { synchronous = false } = eventsAndSyncs;
    const { submitConfig } = this.state;
    const { isLifecycle, sendLCOEventsToParent } = this.props;

    const isCancelEvent = eventsAndSyncs.events?.some(
      (e) => e.event === 'cancel'
    );
    if (isCancelEvent && this.showRefundModalIfNeeded()) {
      return;
    }

    if (isPreview) {
      if (
        eventsAndSyncs.syncs?.length &&
        eventsAndSyncs.syncs.find(
          (s) => s.action === 'link' || s.action === 'confirm_cancel'
        )
      ) {
        BlackToastTrigger({ content: 'User would follow this action.' });
      }
      return;
    }
    const events = eventsAndSyncs.events?.map((evt) => ({
      ...evt,
      source: isPricingPage
        ? EventSource.PRICING_PAGE
        : isLifecycle
          ? EventSource.LIFECYCLE_OFFER
          : undefined,
    }));
    const syncs = eventsAndSyncs.syncs?.map((evt) => ({
      ...evt,
      source: isLifecycle ? EventSource.LIFECYCLE_OFFER : undefined,
    }));
    const payload = {
      ...eventsAndSyncs,
      events,
      syncs,
      config: submitConfig,
    };
    sendLCOEventsToParent?.(payload);
    const isCheckoutProcessing = payload.syncs?.some(
      (evt) => evt.isCheckoutProcessing
    );
    if (isCheckoutProcessing) {
      return;
    }
    if (synchronous) {
      this.setState({
        showOpaqueLoader: true,
      });
      await Events.executeEventQueue(payload);
      const isRedirect = payload.syncs?.some(
        (s) => s.action === RestModalAction.link && _.isString(s.url)
      );
      if (!isRedirect) {
        setTimeout(
          () =>
            this.setState({
              showOpaqueLoader: false,
            }),
          2000
        );
      }
    } else {
      Events.executeEventQueue(payload);
    }
  };

  handleBackwards = (backObj: BackwardsObject) => {
    const { session, version, user } = this.props;
    const { survey } = this.state;
    const syncObj: EventSyncObject = {
      session,
      version,
      app_id: (user && user.app_id) || null,
      survey_state: survey,
      ...backObj,
    };

    const eventObj: EventObject = {
      session,
      version,
      app_id: (user && user.app_id) || null,
      id: backObj.component.id,
      ...backObj,
      event: 'nevermind',
      properties: {
        survey_state: survey,
      },
    };

    this.executeEvents({
      syncs: [syncObj],
      events: [eventObj],
      synchronous: true,
    });
  };

  updateCompetitionOtherText: CompetitorOtherTextHandler = (
    value,
    type = { type: 'change' }
  ) => {
    const { survey } = this.state;
    if (_.get(type, 'type') === 'change') {
      this.setState({
        survey: {
          ...survey,
          competition_other: value,
        },
      });
    } else if (_.get(type, 'type') === 'track') {
      this.handleTrackEvent(
        { target: { type: 'blur' } },
        value,
        'competition_other'
      );
    }
  };

  // As survey is filled out, values are stored via this
  // Only for multi-tier
  handleResponseMulti: SurveyMultiRowResponseHandler = (
    e,
    value,
    tier,
    currentTier,
    type = { type: 'change' }
  ) => {
    const key = (e.target.name || tier)!;
    const { survey } = this.state;

    // if it's not competition,sentiment, feedback, confirmation
    // then it's a reason  for leaving
    // reason_for_leaving: [], // this is now an array of reasons
    //       competition: '',
    //       sentiment: null,
    //       feedback: '',
    //       confirmation: ''
    if (key === 'feedback') {
      const typeVal = _.get(type, 'type');
      if (typeVal === 'change') {
        this.setState({
          survey: {
            ...survey,
            [key]: value as string,
          },
        });
      } else if (typeVal === 'track') {
        this.handleTrackEvent(e, value, key);
      }
    } else if (key === 'sentiment') {
      this.setState(
        {
          survey: {
            ...survey,
            [key]: Number(value),
          },
        },
        () => this.handleTrackEvent(e, value, key)
      );
    } else if (key === 'competition' || key === 'confirmation') {
      this.setState(
        {
          survey: {
            ...survey,
            [key]: value,
          },
        },
        () => this.handleTrackEvent(e, value, key)
      );
    } else if (key === 'reason_for_leaving') {
      this.updateReasonsForLeaving(key, value as string, tier!, currentTier!);
      this.handleTrackEvent(e, value, key);
    }
  };

  // Reasons for Leaving
  // Save the answers into that array
  updateReasonsForLeaving = (
    name: string,
    value: string,
    tier: string,
    livesOn: string
  ) => {
    const { survey } = this.state;
    // get the array, check for its presence
    // insert it if not there, remove it if need to
    const entry = {
      name,
      value,
      tier,
      lives_on: livesOn, // needs to be camelcase for sync event reasons
    };
    const index = _.findIndex(survey.reason_for_leaving, {
      name,
      lives_on: livesOn,
    });
    const surveyReasons = survey.reason_for_leaving;
    if (index === -1) {
      surveyReasons.push(entry);
    } else {
      surveyReasons.splice(index, 1, entry);
    }
    this.setState({
      survey: { ...survey, reason_for_leaving: surveyReasons },
    });
  };

  // Clears other text value
  // when used in the reason_for_leaving questions surveySection.requires.multi_row_survey
  clearCompetitionOtherText: CompetitorOtherTextClearHandler = (cb) => {
    const { survey: stateSurvey } = this.state;

    const survey = { ...stateSurvey };
    delete survey.competition_other;
    this.setState(
      {
        survey,
      },
      () => {
        if (cb) {
          cb();
        }
      }
    );
  };

  // Reason for leaving other value
  handleReasonOtherText: ReasonOtherHandler = (
    value,
    name,
    tier,
    event,
    type = { type: 'change' }
  ) => {
    // update reasons for leaving array with other selected
    // add in reason_other in the array to hold the value that is typed
    const { survey } = this.state;
    if (_.get(type, 'type') === 'track') {
      this.handleTrackEvent(event, value, name);
    } else if (_.get(type, 'type') === 'change') {
      this.setState({
        survey: {
          ...survey,
          [name]: value,
        },
      });
    }
  };

  // Clears reason for leaving other value
  clearReasonOtherText: ReasonOtherTextClearHandler = (cb) => {
    const { survey } = this.state;
    this.setState(
      {
        survey: {
          ...survey,
          reason_other: undefined,
        },
      },
      () => {
        if (cb) {
          cb();
        }
      }
    );
  };

  // Track any entered information throughout the survey
  handleAnyInformation: ModalHandleAnyInfo = ({ modal, message }) => {
    const { survey } = this.state;
    const existingResponses = survey.responses || {};
    this.setState({
      survey: {
        ...survey,
        responses: {
          ...existingResponses,
          [modal]: message,
        },
      },
    });
  };

  showRefundModalIfNeeded = (): boolean => {
    const { modal: currentModal, hasShownRefundModal } = this.state;
    const { layout } = this.props;

    if (
      hasShownRefundModal ||
      currentModal?.id === CANCEL_WITH_REFUND_MODAL_ID
    ) {
      return false;
    }

    const refundModal = (layout?.cards || []).find(
      (card: any) =>
        card.id === CANCEL_WITH_REFUND_MODAL_ID &&
        card.template === RestModalTemplates.modal_refund_cancel
    );

    if (refundModal) {
      this.setState({ modal: refundModal as RestModalCard, showModal: true });
      return true;
    }
    return false;
  };

  // set the state of the upcoming modal
  nextStep: NextStepHandler = (modalKey, extra) => {
    const { cards, modal: currentModal } = this.state;

    if (modalKey === 'confirm_cancel') {
      if (currentModal?.id === CANCEL_WITH_REFUND_MODAL_ID) {
        this.setState({ hasShownRefundModal: true });
      } else if (this.showRefundModalIfNeeded()) {
        return;
      }
    }

    this.setState({
      modal: modalKey
        ? (cards.get(modalKey) as RestModalCard)
        : ({} as RestModalCard),
    });
  };

  // shows the modal
  continueFn = () => {
    this.setState({
      showModal: true,
    });
  };

  // closes modal
  nevermind: ModalOnClose = (reason) => {
    this.setState({
      closeModalReason: reason,
      showModal: false,
      modal: {} as RestModalCard,
    });
  };

  showLoaderOverlay = () => {
    const { isPreview } = this.context;
    if (isPreview) {
      return;
    }
    this.setState({
      showLoader: true,
    });
  };

  directFromSurveyCancel: DirectFromSurveyCancelHandler = (action) => {
    if (this.showRefundModalIfNeeded()) {
      return;
    }

    // loader locks page while submission occuring
    this.showLoaderOverlay();
    const { session, id, name, version, user } = this.props;
    const { survey, cardsByTemplate } = this.state;
    const { url, type, sync, validation } = action;

    const eventObj: EventObject = {
      session,
      id,
      name: `direct_click_cancel_${name}`,
      version,
      app_id: (user && user.app_id) || null,
      form: sync,
      event: 'cancel',
      card_version: cardsByTemplate.get('multi_row_survey')?.version,
      properties: {
        survey_state: survey,
        sync,
      },
    };
    const syncObj: EventSyncObject = {
      session,
      name: `direct_click_cancel_${name}`,
      version,
      app_id: (user && user.app_id) || null,
      form: sync,
      direction: 'forwards',
      action: RestModalAction.link,
      id,
      validation,
      url,
      type,
      survey_state: survey,
    };
    // leave loader up when the page leaves
    this.executeEvents({
      events: [eventObj],
      syncs: [syncObj],
      synchronous: true,
    });
  };

  renderFullWidthHeader() {
    const fullWidthHeaderCard =
      this.getCardByTemplate<ExperienceFullWidthHeaderCard>(
        'full_width_header'
      );

    return (
      fullWidthHeaderCard && (
        <FullWidthHeader card={fullWidthHeaderCard} fullInfo={this.props} />
      )
    );
  }

  renderSimpleHeader() {
    const simpleHeaderCard =
      this.getCardByTemplate<ExperienceSimpleHeaderCard>('simple_header');
    return (
      simpleHeaderCard && (
        <SimpleHeader card={simpleHeaderCard} fullInfo={this.props} />
      )
    );
  }

  getCardByTemplate<T>(template: keyof typeof ExperienceCardTemplate) {
    const { cardsByTemplate } = this.state;
    return cardsByTemplate.get(template) as Maybe<T>;
  }

  renderSimplePlea() {
    const { cardsByTemplate } = this.state;
    const simplePleaCard =
      this.getCardByTemplate<ExperienceSimplePleaCard>('simple_plea');
    return (
      simplePleaCard && (
        <Plea
          card={simplePleaCard}
          fullWidthHeader={cardsByTemplate.has('full_width_header')}
        />
      )
    );
  }

  renderOfferCardPack() {
    const { content, survey } = this.state;
    const { session, version, user } = this.props;
    const card = content.find(
      (x) => x.template === RestModalTemplates.offer_card_pack
    );
    return (
      <OfferCardpackRenderer
        card={card ?? ({} as RestOfferCardPack)}
        executeEvents={this.executeEvents}
        syncData={{
          session,
          version,
          app_id: (user && user.app_id) || null,
          survey_state: survey,
        }}
        fullInfo={this.props}
        nextStep={this.nextStep}
        continueAction={this.continueFn}
        nevermind={this.nevermind}
      />
    );
  }

  renderPricingTable() {
    const { isPreview, pricingPageProps } = this.context;
    const discounts = pricingPageProps?.discounts;
    const subscription = pricingPageProps?.subscription;
    const pricingTable = isPreview
      ? (_.get(
          this.props.layout,
          'pricing_table'
        ) as unknown as PricingTableType)
      : PricingTableHelper.getPricingTableFromLayout(
          this.props.layout as unknown as RestPricingPageLayout
        );
    return (
      <PricingTable
        pricingTable={pricingTable}
        subscription={subscription}
        discounts={discounts}
        country={this.props.country}
        defaultCurrency={pricingPageProps?.defaultCurrency}
        executeEvents={this.executeEvents}
      />
    );
  }

  renderPricingPageHeader() {
    const { isPreview } = this.context;
    // Preview reads live layout; Funnel card state can lag form edits.
    const headerCard = isPreview
      ? (this.props.layout.cards.find(
          (card) => card.template === 'simple_header'
        ) as Maybe<PricingPageHeaderCard>)
      : this.getCardByTemplate<PricingPageHeaderCard>('simple_header');
    return headerCard && <PricingPageHeader card={headerCard} />;
  }

  renderHostedFooter() {
    const hostedFooterCard =
      this.getCardByTemplate<ExperienceHostedFooterCard>('simple_footer');
    return hostedFooterCard && <HostedFooter card={hostedFooterCard} />;
  }

  renderLoss() {
    const { content, survey } = this.state;
    const {
      session,
      version,
      user,
      lossAversionLayout,
      isLossAversionLoading,
      lossAversionErrors,
    } = this.props;
    return (
      <Loss
        cards={content}
        executeEvents={this.executeEvents}
        syncData={{
          session,
          version,
          app_id: (user && user.app_id) || null,
          survey_state: survey,
        }}
        nextStep={this.nextStep}
        continueAction={this.continueFn}
        lossAversionLayout={lossAversionLayout}
        isLossAversionLoading={isLossAversionLoading}
        lossAversionErrors={lossAversionErrors}
      />
    );
  }

  renderSimplePleaWithReturn() {
    const { survey } = this.state;
    const { session, version, user } = this.props;
    const simplePleaWithReturnCard =
      this.getCardByTemplate<ExperienceSimplePleaWithReturnCard>(
        'simple_plea_with_return'
      );

    return (
      simplePleaWithReturnCard && (
        <Warn
          card={simplePleaWithReturnCard}
          executeEvents={this.executeEvents}
          syncData={{
            session,
            version,
            app_id: (user && user.app_id) || null,
            survey_state: survey,
          }}
        />
      )
    );
  }

  renderMultiRowSurvey() {
    const { showLoader, survey, localState } = this.state;
    const { isPreview } = this.context;
    const multiRowSurvey =
      this.getCardByTemplate<ExperienceMultiRowSurveyCard>('multi_row_survey');
    return (
      multiRowSurvey && (
        <SurveyMultiRow
          card={multiRowSurvey}
          response={survey}
          handler={this.handleResponseMulti}
          handleReasonOtherText={this.handleReasonOtherText}
          clearReasonOtherText={this.clearReasonOtherText}
          handleCompOtherText={this.updateCompetitionOtherText}
          clearCompOtherText={this.clearCompetitionOtherText}
          nextStep={this.nextStep}
          continue={this.continueFn}
          handleBackwards={this.handleBackwards}
          LS={localState!}
          directFromSurveyCancel={this.directFromSurveyCancel}
          loading={showLoader}
          isPreview={isPreview}
        />
      )
    );
  }

  renderModal() {
    const { isLifecycle } = this.props;
    const {
      showModal,
      modal,
      survey,
      surveySection,
      questions,
      surveyType,
      presentation,
    } = this.state;
    return (
      <Modal
        isLifecycle={isLifecycle}
        open={showModal}
        modal={modal ?? ({} as RestModalCard)}
        nextStep={this.nextStep}
        nevermind={this.nevermind}
        fullInfo={this.props}
        response={survey}
        executeEvents={this.executeEvents}
        sendLCOEventsToParent={this.props.sendLCOEventsToParent}
        fullSurvey={surveySection}
        questions={questions}
        surveyType={surveyType}
        showLoaderOverlay={this.showLoaderOverlay}
        handleAnyInformation={this.handleAnyInformation}
        presentation={presentation}
      />
    );
  }

  render() {
    const { disableThemeProvider, emotionTheme } = this.props;
    if (disableThemeProvider) return this.renderContent();
    return (
      <EmotionThemeProvider theme={emotionTheme}>
        {this.renderContent()}
      </EmotionThemeProvider>
    );
  }

  orderRenderEls = () => {
    const { isHostedPage, isPricingPage } = this.context;

    const pleaCard =
      this.getCardByTemplate<ExperienceSimplePleaCard>('simple_plea');
    const pleaWithReturnCard =
      this.getCardByTemplate<ExperienceSimplePleaWithReturnCard>(
        'simple_plea_with_return'
      );
    const surveyCard =
      this.getCardByTemplate<ExperienceMultiRowSurveyCard>('multi_row_survey');

    const header = isPricingPage ? null : (
      <React.Fragment>
        {this.renderFullWidthHeader()}
        {this.renderSimpleHeader()}
      </React.Fragment>
    );
    const plea = <div className="container">{this.renderSimplePlea()}</div>;
    const laCards = <div className="container">{this.renderLoss()}</div>;
    const pleaWithReturn = (
      <div className="container">{this.renderSimplePleaWithReturn()}</div>
    );
    const survey = (
      <div className="container pb-6">{this.renderMultiRowSurvey()}</div>
    );

    const pricingTable = (
      <div className="pricing-page tw-w-full">{this.renderPricingTable()}</div>
    );

    const pricingPageHeader = (
      <div className="pricing-page tw-w-full">
        {this.renderPricingPageHeader()}
      </div>
    );

    const offerCardPack = (
      <div className="container" style={{ maxWidth: '1024px' }}>
        {this.renderOfferCardPack()}
      </div>
    );
    const hostedFooter = (
      <div className="container">{this.renderHostedFooter()}</div>
    );

    const funnelEls = [
      { key: 'header', el: header, order: 100 },
      { key: 'plea', el: plea, order: pleaCard?.order ?? 200 },
      { key: 'la', el: laCards, order: 300 },
      {
        key: 'pleaWithReturn',
        el: pleaWithReturn,
        order: pleaWithReturnCard?.order ?? 400,
      },
      { key: 'survey', el: survey, order: surveyCard?.order ?? 500 },
    ];

    const pricingPageEls = [
      { key: 'header', el: pricingPageHeader, order: 100 },
      { key: 'pricingTable', el: pricingTable, order: 200 },
    ];

    const hostedEls = [
      { key: 'header', el: header, order: 100 },
      { key: 'plea', el: plea, order: 200 },
      { key: 'offerCardPack', el: offerCardPack, order: 300 },
      { key: 'hostedFooter', el: hostedFooter, order: 400 },
    ];

    return chain(
      !isPricingPage ? funnelEls : isHostedPage ? hostedEls : pricingPageEls
    )
      .sortBy((x) => x.order)
      .value();
  };

  renderContent() {
    const showRedirectMsg = window.bbkFeatureFlags?.includes(
      FeatureFlagsE.SHOW_FUNNEL_REDIRECT_MESSAGE
    );
    const { layout, isLifecycle, emotionTheme } = this.props;
    const { showLoader, localState, showOpaqueLoader } = this.state;
    const { isPreview } = this.context;

    if (!localState) {
      return null;
    }

    if (showLoader) {
      return <LoadingOverlay />;
    }

    const renderEls = this.orderRenderEls();

    const { isHostedPage, isPricingPage } = this.context;
    const hideEls = isLifecycle && !isHostedPage;

    return (
      <Container funnel={{ layout }} isPricingPage={isPricingPage}>
        {showOpaqueLoader && (
          <LoadingOverlay
            message={
              showRedirectMsg
                ? 'Please wait — we’re taking you to the next page.'
                : 'Loading...'
            }
            opaque
            opaqueFull={isLifecycle}
          />
        )}
        {!hideEls &&
          renderEls.map((x) => (
            <React.Fragment key={x.key}>{x.el}</React.Fragment>
          ))}
        <div className="container">{this.renderModal()}</div>
        <Global
          styles={css`
            ${isPreview ? '.exit-survey' : 'body'} {
              line-height: 1.15;
            }

            ${isPreview ? '' : 'body,'} .bbk-designer-page-experience {
              background: ${emotionTheme.background};
              ${emotionTheme[ThemeKeys.GLOBAL_CSS]}
            }

            ${isPreview ? '.exit-survey, .BrightBox ' : 'body '} {
              ${emotionTheme[ThemeKeys.GLOBAL_CSS]}
            }
            :root {
              --pricing-page-headline-color: ${emotionTheme.titleFontColor};
              --pricing-page-text-font-text-color: ${emotionTheme.textColor};
              --pricing-page-font-family: ${emotionTheme.fontFamily};
              --pricing-page-button-bg-color: ${
                emotionTheme.buttonBackgroundColor || emotionTheme.primaryColor
              };
              --pricing-page-button-text-color: ${emotionTheme.buttonTextColor};
              --pricing-page-card-background-color: ${emotionTheme.cardBackground};
              --pricing-page-background-color: ${emotionTheme.background};
              --pricingtable-border-radius: ${emotionTheme.buttonBorderRadius}px;
              --pricingtable-badge-color: ${
                emotionTheme.offerCardBadgeColors?.[0]
              };
              --pricingtable-style-accent-color: ${emotionTheme.primaryColor};
            }
            .pricing-page {
              font-family: var(--pricing-page-font-family);
            }
            ${
              !isLifecycle
                ? ''
                : `
              body {
                background: transparent !important;
              }
              .exit-survey {
                padding: 0;
              }
              .bbk-loss-components {
                display: none;
              }
            `
            }
          `}
        />
      </Container>
    );
  }
}

interface Funnel {
  context: React.ContextType<typeof FunnelContext>;
}

export default Funnel;
